import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaChartBar,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import Serviceimg1 from '../images/homepage/home3.png';
import Serviceimg2 from '../images/homepage/home4.png';
import Serviceimg3 from '../images/homepage/home5.png';
import Serviceimg4 from '../images/homepage/home6.png';

// Styled Components
const CarouselContainer = styled.div`
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 3rem 0.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const Header = styled.div`
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const Title = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.2rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const CarouselTrack = styled.div`
  width: 100%;
  max-width: 900px;
  position: relative;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 20px;
    margin: 0 1rem;
  }

  @media (max-width: 480px) {
    margin: 0 0.5rem;
  }
`;

const SlideContainer = styled.div`
  display: flex;
  width: 400%;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Slide = styled.div<{ bgColor: string }>`
  width: 25%;
  background: ${props => props.bgColor};
  border-radius: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    border-radius: 20px;
  }
`;

const SlideContent = styled.div`
  display: flex;
  align-items: center;
  padding: 3rem;
  gap: 3rem;
  min-height: 500px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1.5rem;
    justify-content: center;
    min-height: 480px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    gap: 1rem;
    min-height: 460px;
  }
`;

const SlideText = styled.div`
  flex: 1;
  text-align: left;
  color: white;

  @media (max-width: 768px) {
    text-align: center;
    flex: none;
  }

  h3 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.8rem;
      margin-bottom: 1rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
      margin-bottom: 0.8rem;
    }
  }

  p {
    font-size: 1.2rem;
    line-height: 1.6;
    opacity: 0.95;
    margin: 0;

    @media (max-width: 768px) {
      font-size: 1rem;
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }
`;

const SlideImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    flex: none;
  }

  img {
    max-width: 100%;
    max-height: 350px;
    border-radius: 16px;
    object-fit: contain;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
      max-height: 200px;
      border-radius: 12px;
    }

    @media (max-width: 480px) {
      max-height: 180px;
    }
  }
`;

const NavButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4f46e5;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  z-index: 10;

  @media (max-width: 768px) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }

  svg {
    font-size: 20px;

    @media (max-width: 480px) {
      font-size: 16px;
    }
  }

  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
      transform: translateY(-50%) scale(1.05);
    }
  }

  &:active {
    transform: scale(0.95);

    @media (max-width: 768px) {
      transform: translateY(-50%) scale(0.95);
    }
  }
`;

const PrevButton = styled(NavButton)`
  @media (max-width: 768px) {
    left: 1rem;
  }

  @media (max-width: 480px) {
    left: 0.5rem;
  }
`;

const NextButton = styled(NavButton)`
  @media (max-width: 768px) {
    right: 1rem;
  }

  @media (max-width: 480px) {
    right: 0.5rem;
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 3rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-top: 2rem;
    gap: 0.8rem;
  }

  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const Indicator = styled(motion.button)<{ active: boolean; bgColor: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid ${props => props.active ? props.bgColor : 'rgba(255, 255, 255, 0.3)'};
  background: ${props => props.active ? props.bgColor : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }

  svg {
    font-size: 24px;
    color: ${props => props.active ? 'white' : '#64748b'};
    z-index: 1;
    position: relative;
    filter: ${props => props.active ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' : 'none'};

    @media (max-width: 768px) {
      font-size: 20px;
    }

    @media (max-width: 480px) {
      font-size: 18px;
    }
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

    @media (hover: none) {
      transform: none;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;

// Types
interface CarouselItem {
  id: string;
  title: string;
  description: string;
  image: string;
  bgColor: string;
  icon: React.ComponentType;
  indicatorBg: string;
}

interface FeatureCarouselProps {
  className?: string;
  autoPlayInterval?: number;
  pauseOnHover?: boolean;
}

// Carousel data
const carouselItems: CarouselItem[] = [
  {
    id: 'scheduling',
    title: "Smart Scheduling",
    description: "Optimize your team's time with intelligent scheduling that adapts to your business needs and maximizes efficiency.",
    image: Serviceimg1,
    bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    icon: FaCalendarAlt,
    indicatorBg: "#667eea"
  },
  {
    id: 'tracking',
    title: "Real-time Tracking", 
    description: "Monitor your team's progress and locations in real-time for better coordination and improved service delivery.",
    image: Serviceimg2,
    bgColor: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    icon: FaMapMarkerAlt,
    indicatorBg: "#f093fb"
  },
  {
    id: 'management',
    title: "Customer Management",
    description: "Keep all customer information organized and accessible from anywhere with our comprehensive CRM system.",
    image: Serviceimg3,
    bgColor: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    icon: FaUsers,
    indicatorBg: "#4facfe"
  },
  {
    id: 'analytics',
    title: "Analytics Dashboard",
    description: "Get insights into your business performance with detailed analytics, reports, and actionable data visualizations.",
    image: Serviceimg4,
    bgColor: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    icon: FaChartBar,
    indicatorBg: "#43e97b"
  }
];

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({ 
  className, 
  autoPlayInterval = 5000,
  pauseOnHover = true 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoPlaying || isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isPaused, autoPlayInterval]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const handleMouseEnter = () => {
    if (pauseOnHover) setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) setIsPaused(false);
  };

  return (
    <CarouselContainer className={className}>
      <Container>
        <Header>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Title>Experience ServTrackr in Action</Title>
            <Subtitle>
              Discover how our platform transforms service businesses with powerful, intuitive features.
            </Subtitle>
          </motion.div>
        </Header>
        
        <CarouselWrapper>
          <PrevButton
            onClick={prevSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaChevronLeft />
          </PrevButton>
          
          <CarouselTrack
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <SlideContainer
              style={{
                transform: `translateX(-${currentSlide * 25}%)`
              }}
            >
              {carouselItems.map((item, index) => (
                <Slide key={item.id} bgColor={item.bgColor}>
                  <SlideContent>
                    <SlideText>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </SlideText>
                    <SlideImage>
                      <img
                        src={item.image}
                        alt={item.title}
                      />
                    </SlideImage>
                  </SlideContent>
                </Slide>
              ))}
            </SlideContainer>
          </CarouselTrack>
          
          <NextButton
            onClick={nextSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaChevronRight />
          </NextButton>
        </CarouselWrapper>
        
        <Indicators>
          {carouselItems.map((item, index) => (
            <Indicator
              key={item.id}
              active={index === currentSlide}
              bgColor={item.indicatorBg}
              onClick={() => goToSlide(index)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <item.icon />
            </Indicator>
          ))}
        </Indicators>
      </Container>
    </CarouselContainer>
  );
};

export default FeatureCarousel;
